import styled from "styled-components";
import { pxToRem } from "../../styles/utils";

export const SupportResponsePageContainer = styled.div`
  display: flex;
  height: calc(100% - ${pxToRem(24)});
  margin: ${pxToRem(12)};
`;

export const ContentWrapper = styled.div`
  min-width: 0;
  flex: 1;
  box-sizing: border-box;
  overflow: auto;
  padding: ${pxToRem(28)} ${pxToRem(32)} ${pxToRem(48)};
  border-radius: ${({ theme }) => pxToRem(theme.radii.lg)};
  background: ${({ theme }) => theme.colors.white};
  box-shadow: ${({ theme }) => theme.shadows.md}, ${({ theme }) => theme.shadows.ring};

  @media (max-width: 720px) {
    padding: ${pxToRem(20)};
  }
`;

export const ResponseBody = styled.main`
  width: 100%;
  max-width: ${pxToRem(950)};
`;

export const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${pxToRem(8)};
  padding: 0;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.inkSoft};
  font: inherit;
  font-size: ${pxToRem(16)};
  font-weight: 400;
  line-height: 1.35;
  cursor: pointer;
  transition: color 0.15s ease;

  svg {
    width: ${pxToRem(19)};
    height: ${pxToRem(19)};
    fill: none;
    stroke: currentColor;
    stroke-width: 1.8;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  &:hover,
  &:focus-visible {
    color: ${({ theme }) => theme.colors.brand};
  }
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${pxToRem(15)};
  margin: ${pxToRem(18)} 0 ${pxToRem(8)};
`;

export const Title = styled.h1`
  margin: 0;
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${pxToRem(29)};
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: -0.02em;
`;

export const StatusBadge = styled.span<{ $answered: boolean }>`
  display: inline-flex;
  align-items: center;
  min-height: ${pxToRem(31)};
  padding: 0 ${pxToRem(13)};
  border-radius: ${pxToRem(9)};
  background: ${({ $answered }) =>
    $answered ? "rgba(86, 189, 116, 0.14)" : "rgba(194, 134, 31, 0.14)"};
  color: ${({ $answered, theme }) => ($answered ? theme.colors.success : "#c2861f")};
  font-size: ${pxToRem(15)};
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
`;

export const Meta = styled.div`
  margin-bottom: ${pxToRem(28)};
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${pxToRem(16)};
  line-height: 1.4;
`;

export const Label = styled.div`
  margin-bottom: ${pxToRem(9)};
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${pxToRem(15)};
  font-weight: 400;
  line-height: 1.35;
`;

export const ResponseText = styled.div`
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${pxToRem(19)};
  font-weight: 400;
  line-height: 1.6;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

export const QuestionBlock = styled.section`
  margin-bottom: ${pxToRem(23)};
  padding: ${pxToRem(20)} ${pxToRem(23)};
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
  border-radius: ${pxToRem(18)};
  background: ${({ theme }) => theme.colors.white};
`;

export const AttachmentLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${pxToRem(11)};
  max-width: 100%;
  margin-top: ${pxToRem(18)};
  padding: ${pxToRem(10)} ${pxToRem(14)};
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${pxToRem(16)};
  line-height: 1.35;
  text-decoration: none;
  overflow-wrap: anywhere;
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease,
    color 0.15s ease;

  svg {
    flex: 0 0 auto;
    width: ${pxToRem(15)};
    height: ${pxToRem(20)};
    color: ${({ theme }) => theme.colors.brand};
  }

  &:hover,
  &:focus-visible {
    border-color: ${({ theme }) => theme.colors.brand};
    background: ${({ theme }) => theme.colors.brandSubtle};
    color: ${({ theme }) => theme.colors.brandDark};
  }
`;

export const AnswerBlock = styled.section`
  padding: ${pxToRem(20)} ${pxToRem(23)};
  border: ${pxToRem(1)} solid rgba(114, 82, 220, 0.35);
  border-left-width: ${pxToRem(4)};
  border-radius: ${pxToRem(5)} ${pxToRem(18)} ${pxToRem(18)} ${pxToRem(5)};
  background: ${({ theme }) => theme.colors.brandSoft};
`;

export const AnswerDate = styled.div`
  margin-bottom: ${pxToRem(10)};
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${pxToRem(15)};
  line-height: 1.35;
`;

export const WaitingBlock = styled.section`
  padding: ${pxToRem(25)} ${pxToRem(23)};
  border: ${pxToRem(1)} dashed ${({ theme }) => theme.borders.outline};
  border-radius: ${pxToRem(18)};
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${pxToRem(18)};
  line-height: 1.45;
`;

export const PageState = styled.div`
  display: flex;
  min-height: ${pxToRem(180)};
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${pxToRem(18)};
`;
